/**
 * Slack-style grouping for transcript lines (#11418). Mirrors the notetaker
 * desktop app's copy so the same transcript reads the same in both, the way
 * `transcriptTimestamp` is already kept per-app.
 *
 * A transcript line arrives every few seconds, so stamping each one with its
 * own speaker label and wall-clock time (#10002) buries the words in chrome.
 * Consecutive lines from one speaker instead form a group: the first carries
 * the header, the rest carry none, and their exact time stays available on
 * hover. A speaker change or a real gap in the conversation starts a new group.
 */
export const TRANSCRIPT_GROUP_WINDOW_MS = 5 * 60 * 1000;

export interface TranscriptGroupingLine {
  /** Who is speaking. Consecutive lines from different speakers never group. */
  speaker: string;
  /**
   * Wall-clock capture time — an ISO string from the stored utterance rows, or
   * epoch ms. Null / 0 / unparseable means the line has no timing at all, which
   * is the case whenever the rows don't line up with the parsed transcript.
   */
  capturedAt: string | number | null;
}

/** Epoch ms, or null when the value carries no usable timing. */
function toEpochMs(capturedAt: string | number | null): number | null {
  if (capturedAt === null || capturedAt === "" || capturedAt === 0) return null;
  const ms = new Date(capturedAt).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/**
 * Whether `current` opens a new group — i.e. whether it should render the
 * speaker + timestamp header rather than folding into the line above it.
 */
export function startsTranscriptGroup(
  previous: TranscriptGroupingLine | null | undefined,
  current: TranscriptGroupingLine,
  windowMs: number = TRANSCRIPT_GROUP_WINDOW_MS
): boolean {
  if (!previous) return true;
  if (previous.speaker !== current.speaker) return true;

  const previousMs = toEpochMs(previous.capturedAt);
  const currentMs = toEpochMs(current.capturedAt);
  // Without a time on both sides there is no gap to measure, and nothing to
  // stamp either — group on the speaker alone rather than re-printing a header
  // that would carry no time.
  if (previousMs === null || currentMs === null) return false;

  return Math.abs(currentMs - previousMs) > windowMs;
}
